import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, ListGroup, Alert, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faTimes, faSave, faUser, faUserMd, faPills, faReceipt, faCancel, faMapMarkerAlt, faPlus, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import api from '../services/api';
import money from '../assets/img/money-selected.svg';
import card from '../assets/img/credit-card-selected.svg';
import pix from '../assets/img/pix.png';
import moment from 'moment';
import { useStateContext } from '../contexts/ContextProvider';
import apiFormData from '../services/apiFormData';
import Loading from './Loading';
import Globals from '../Globals';
import { formatCEP, formatCurrency } from '../utils/formats';
import parseMaskedMoney from '../utils/parseMaskedMoney';
import delivery from '../assets/img/entrega-rapida.png';

export default function AttendancePaymentModal({ show, attendance, onHide }) {
  const { user } = useStateContext();

  const [currentStep, setCurrentStep] = useState(1);

  const [loading, setLoading] = useState(false);
  const [medicines, setMedicines] = useState([]);
  const [paidmedicines, setPaidMedicines] = useState([]);
  const [notPaidMedicines, setNotPaidMedicines] = useState([]);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState({
    cash: '',
    card: '',
    pix: ''
  });
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [validationError, setValidationError] = useState('');

  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState({
    street: '',
    number: '',
    district: '',
    city: '',
    state: '',
    complement: '',
    zipcode: '',
    default: false
  });
  const [addressFormErrors, setAddressFormErrors] = useState({});
  const [savingAddress, setSavingAddress] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState('');
  const [showMapModal, setShowMapModal] = useState(false);

  useEffect(() => {
    const getMedicines = async () => {
      if (!show) return;

      setLoading(true);

      await api.post('api/payment/prescription/pharmacy/items', {
        attendance_id: attendance.attendance_id,
        shopper_id: attendance.shopper_id
      })
        .then(response => {
          setMedicines(response.data.data.schedulings.data)
          const medicines = response.data.data.schedulings.data
          setPaidMedicines(medicines.filter(med => med.pharmacy_drug_flag_paid));
          setNotPaidMedicines(medicines.filter(med => !med.pharmacy_drug_flag_paid));
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        })
    }

    getMedicines();
  }, [show])

  useEffect(() => {
    const getAddresses = async () => {
      if (!show) return;

      await api.get(`api/search/address/customers/${attendance.customer_id}`)
        .then(response => {
          const addresses = response.data.data

          setAddresses(addresses)
          //setAddresses([])

          const defaultAddress = addresses.find(addr => addr.default);

          if (defaultAddress) {
            setSelectedAddress(defaultAddress.id);
          }
        })
        .catch(error => {
          console.log(error);
        })
    }

    getAddresses();
  }, [show]);

  const handleNextStep = () => {
    if (currentStep === 1 && validateForm()) {
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const formatMoney = (value) => {
    return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
  }

  const calculateTotal = () => {
    const total = selectedMedications.reduce((total, med) => {
      return total + (Number(med.pharmacy_drug_quantity * med.pharmacy_drug_price) || 0);
    }, 0);

    return Math.round(total * 100) / 100;
  };

  const calculatePaymentTotal = () => {
    const cash = parseMaskedMoney(paymentMethods.cash);
    const card = parseMaskedMoney(paymentMethods.card);
    const pix = parseMaskedMoney(paymentMethods.pix);
    return cash + card + pix;
  };

  const calculateRemaining = () => {
    const medicationTotal = calculateTotal();
    const paymentTotal = calculatePaymentTotal();

    return medicationTotal - paymentTotal;
  };

  const handleMedicationChange = (medicine) => {
    setSelectedMedications(prev => {
      const exists = prev.some(m => m.id === medicine.id);
      if (exists) {
        return prev.filter(m => m.id !== medicine.id);
      } else {
        return [...prev, medicine];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedMedications.length === notPaidMedicines.length) {
      // Se todos estão selecionados, deselecionar todos
      setSelectedMedications([]);
    } else {
      // Caso contrário, selecionar todos
      setSelectedMedications(notPaidMedicines);
    }
  };

  const handlePaymentChange = (method, value) => {
    setPaymentMethods(prev => ({
      ...prev,
      [method]: formatCurrency(value)
    }));
  };

  const handleFileAttach = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map(file => ({
      file: file,
      paymentMethod: '' // Inicialmente sem método de pagamento selecionado
    }));
    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const removeAttachment = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleFilePaymentMethodChange = (index, paymentMethod) => {
    setAttachedFiles(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, paymentMethod } : item
      )
    );
  };

  const getPaymentMethodOptions = () => {
    const options = [];

    // Sempre incluir dinheiro como opção
    options.push({ value: 'cash', label: 'Dinheiro', icon: money });

    if (paymentMethods.card && parseMaskedMoney(paymentMethods.card) > 0) {
      options.push({ value: 'card', label: 'Cartão', icon: card });
    }
    if (paymentMethods.pix && parseMaskedMoney(paymentMethods.pix) > 0) {
      options.push({ value: 'pix', label: 'PIX', icon: pix });
    }

    return options;
  };

  const getPaymentMethodLabel = (method) => {
    const options = {
      'cash': 'Dinheiro',
      'card': 'Cartão',
      'pix': 'PIX',
      'courtesy': 'Cortesia'
    };
    return options[method] || 'Selecionar';
  };

  const valuesAreNotEqual = (a, b) => {
    return Math.abs(a - b) > 0.001
  }

  const validateForm = () => {
    const medicationTotal = calculateTotal();
    const paymentTotal = calculatePaymentTotal();
    const remaining = calculateRemaining();

    if (paymentTotal === 0) {
      setValidationError('Informe pelo menos uma forma de pagamento');
      return false;
    }

    if (valuesAreNotEqual(medicationTotal, paymentTotal)) {
      if (remaining > 0) {
        setValidationError(`Ainda falta R$ ${remaining.toFixed(2).replace('.', ',')} para completar o pagamento.`);
      } else {
        setValidationError(`Há um excedente de R$ ${Math.abs(remaining).toFixed(2).replace('.', ',')} no pagamento.`);
      }
      return false;
    }

    // Validação de método de pagamento para cada arquivo - apenas cartão e pix são obrigatórios
    const cardValue = parseMaskedMoney(paymentMethods.card);
    const pixValue = parseMaskedMoney(paymentMethods.pix);

    if (attachedFiles.length === 0 && (cardValue !== 0 || pixValue !== 0)) {
      setValidationError('É obrigatório anexar pelo menos um comprovante de pagamento para Cartão e/ou PIX.');
      return false;
    }

    if (cardValue > 0) {
      const cardFiles = attachedFiles.filter(file => file.paymentMethod === 'card');
      if (cardFiles.length === 0) {
        setValidationError('É obrigatório anexar comprovante para pagamento com Cartão.');
        return false;
      }
    }

    if (pixValue > 0) {
      const pixFiles = attachedFiles.filter(file => file.paymentMethod === 'pix');
      if (pixFiles.length === 0) {
        setValidationError('É obrigatório anexar comprovante para pagamento com PIX.');
        return false;
      }
    }

    return true;
  };

  const handleAddressSelection = (addressId) => {
    setSelectedAddress(addressId);
  };

  const handleNewAddress = () => {
    setShowAddressModal(true);
  };

  const formatCEPInput = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const handleAddressFormChange = (field, value) => {
    setAddressForm(prev => ({
      ...prev,
      [field]: typeof value === 'string' ? value.toUpperCase() : value
    }));

    // Limpar erro do campo quando começar a digitar
    if (addressFormErrors[field]) {
      setAddressFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateAddressForm = () => {
    const errors = {};

    if (!addressForm.street.trim()) errors.street = 'Endereço é obrigatório';
    if (!addressForm.number.trim()) errors.number = 'Número é obrigatório';
    if (!addressForm.district.trim()) errors.district = 'Bairro é obrigatório';
    if (!addressForm.city.trim()) errors.city = 'Cidade é obrigatória';
    if (!addressForm.state.trim()) errors.state = 'Estado é obrigatório';
    if (!addressForm.zipcode.trim()) errors.zipcode = 'CEP é obrigatório';

    setAddressFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAddress = async () => {
    if (!validateAddressForm()) {
      return;
    }

    setSavingAddress(true);

    try {
      await api.post('api/address/customers', {
        customer_id: attendance.customer_id,
        street: addressForm.street,
        number: addressForm.number,
        district: addressForm.district,
        city: addressForm.city,
        state: addressForm.state,
        complement: addressForm.complement,
        zipcode: addressForm.zipcode.replace(/\D/g, ''), // Remove formatação do CEP
        default: addressForm.default
      });

      // Recarregar endereços
      const response = await api.get(`api/search/address/customers/${attendance.customer_id}`);
      const updatedAddresses = response.data.data;
      setAddresses(updatedAddresses);

      // Se o novo endereço foi marcado como padrão, selecioná-lo
      if (addressForm.default) {
        const newDefaultAddress = updatedAddresses.find(addr => addr.default);
        if (newDefaultAddress) {
          setSelectedAddress(newDefaultAddress.id);
        }
      }

      // Fechar modal e limpar formulário
      setShowAddressModal(false);
      setAddressForm({
        street: '',
        number: '',
        district: '',
        city: '',
        state: '',
        complement: '',
        zipcode: '',
        default: false
      });

      Swal.fire({
        title: 'Sucesso!',
        text: 'Endereço cadastrado com sucesso',
        icon: 'success',
        toast: true,
        position: 'top',
        timer: 3000
      });

    } catch (error) {
      console.error(error);
      Swal.fire({
        title: 'Erro!',
        text: 'Ocorreu um erro ao cadastrar o endereço.',
        icon: 'error',
        toast: true,
        position: 'top'
      });
    } finally {
      setSavingAddress(false);
    }
  };

  const handleCancelAddressForm = () => {
    setShowAddressModal(false);
    setAddressForm({
      street: '',
      number: '',
      district: '',
      city: '',
      state: '',
      complement: '',
      zipcode: '',
      default: false
    });
    setAddressFormErrors({});
  };

  // const handleSave = async () => {
  //   const paymentTotal = calculatePaymentTotal();
  //   console.log(Number(paymentTotal.toFixed(2)) + parseMaskedMoney(deliveryFee))
  // }

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    Swal.fire({
      title: 'Aguarde...',
      text: 'Confirmando pagamento...',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      }
    });

    setValidationError('');

    const paymentTotal = calculatePaymentTotal();

    await api.post('api/pharmacy/payments', {
      attendance_id: attendance.attendance_id,
      budget_status: "2",
      customer_id: attendance.customer_id,
      date_payment: moment().format("YYYY-MM-DD HH:mm:ss"),
      total_price: Number(paymentTotal.toFixed(2)) + parseMaskedMoney(deliveryFee),
      total_cash: parseMaskedMoney(paymentMethods.cash),
      total_credit_card: parseMaskedMoney(paymentMethods.card),
      total_pix: parseMaskedMoney(paymentMethods.pix),
      delivery_fee: parseMaskedMoney(deliveryFee),
      total_courtesy: 0, // não há cortesia na venda de medicamentos
      user_id: user.user.id,
      items: medicines.map(medicine => ({
        ...medicine,
        flag_paid: selectedMedications.some(m => m.id === medicine.id)
      }))
    })
      .then(async (response) => {
        // Verifica se existem arquivos anexados para upload
        if (attachedFiles.length > 0) {
          // Criar FormData para envio dos arquivos de comprovante
          let formData = new FormData();

          // Buscar arquivo de comprovante para cada método de pagamento
          const cashFile = attachedFiles.find(item => item.paymentMethod === 'cash')?.file;
          const cardFile = attachedFiles.find(item => item.paymentMethod === 'card')?.file;
          const pixFile = attachedFiles.find(item => item.paymentMethod === 'pix')?.file;
          const courtesyFile = attachedFiles.find(item => item.paymentMethod === 'courtesy')?.file;

          // Adicionar arquivos ao FormData conforme disponibilidade
          if (cashFile) formData.append('image_cash', cashFile);
          if (cardFile) formData.append('image_credit_card', cardFile);
          if (pixFile) formData.append('image_pix', pixFile);
          if (courtesyFile) formData.append('image_courtesy', courtesyFile);

          // Adicionar ID do atendimento para vincular os comprovantes
          formData.append('attendance_id', attendance.attendance_id);

          // Log para debug dos dados que serão enviados
          for (let [key, value] of formData.entries()) {
            console.log(`${key}: ${value}`);
          }

          // Enviar comprovantes para o servidor
          await apiFormData.post('api/pharmacy/image/payments', formData)
            .then((response) => {
              Swal.close();

              // Sucesso no envio dos comprovantes
              Swal.fire({
                title: 'Sucesso!',
                text: 'Pagamento registrado com sucesso',
                icon: 'success',
                showCancelButton: true,
                confirmButtonText: 'Gerar Recibo',
                cancelButtonText: 'Fechar',
                reverseButtons: true
              }).then((result) => {
                if (result.isConfirmed) {
                  handleGenerateReceipt();
                }
                handleCancel();
              });

            }).catch(error => {
              Swal.close();

              // Erro no envio dos comprovantes
              Swal.fire({
                title: 'Erro!',
                text: 'Ocorreu um erro ao enviar comprovante(s) de pagamento.',
                icon: 'error',
                confirmButtonText: 'OK'
              })

              console.error(error)
            })
        } else {
          // Caso não existam arquivos anexados, mostrar sucesso diretamente
          Swal.close();

          Swal.fire({
            title: 'Sucesso!',
            text: 'Pagamento registrado com sucesso',
            icon: 'success',
            showCancelButton: true,
            confirmButtonText: 'Gerar Recibo',
            cancelButtonText: 'Fechar',
            reverseButtons: true
          }).then((result) => {
            if (result.isConfirmed) {
              handleGenerateReceipt();
            }
            handleCancel();
          });
        }
      })
      .catch(error => {
        Swal.close();

        Swal.fire({
          title: 'Erro!',
          text: 'Ocorreu um erro ao registrar o pagamento.',
          icon: 'error',
          confirmButtonText: 'OK'
        })

        console.error(error)
      })
  };

  const handleCancel = () => {
    setCurrentStep(1);
    setSelectedAddress(null);
    setSelectedMedications([]);
    setPaymentMethods({ cash: '', card: '', pix: '' });
    setAttachedFiles([]);
    setValidationError('');
    setShowAddressModal(false);
    setAddressForm({
      street: '',
      number: '',
      district: '',
      city: '',
      state: '',
      complement: '',
      zipcode: '',
      default: false
    });
    setAddressFormErrors({});
    onHide();
  };

  const handleCancelPayment = async () => {
    const result = await Swal.fire({
      title: 'Cancelar Pagamento?',
      text: 'Tem certeza que deseja cancelar este pagamento?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, cancelar!',
      cancelButtonText: 'Não'
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: 'Aguarde...',
        text: 'Cancelando pagamento...',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      await api.post('api/pharmacy/cancel/payments', {
        attendance_id: attendance.attendance_id,
        customer_id: attendance.customer_id,
        date_payment: moment().format("YYYY-MM-DD HH:mm:ss"),
        total_price: null,
        total_cash: null,
        total_credit_card: null,
        total_pix: null,
        total_courtesy: null,
        user_id: user.user.id,
        items: paidmedicines.map(medicine => ({
          pharmacy_drug_id: medicine.pharmacy_drug_id,
          flag_paid: false
        }))
      })
        .then(response => {
          Swal.close();

          Swal.fire({
            title: 'Sucesso!',
            text: 'Pagamento cancelado com sucesso',
            icon: 'success',
            confirmButtonText: 'OK'
          }).then(() => {
            handleCancel();
          });
        })
        .catch(error => {
          Swal.fire({
            title: 'Erro!',
            text: 'Ocorreu um erro ao cancelar o pagamento.',
            icon: 'error',
            confirmButtonText: 'OK'
          })
          console.error(error)
        })
    }
  }

  const handleGenerateReceipt = async () => {
    Swal.fire({
      title: 'Aguarde...',
      text: 'Gerando recibo...',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      }
    });

    await api.post('api/pharmacy/payments/receipt', {
      attendance_id: attendance.attendance_id,
      shopper_id: attendance.shopper_id
    })
      .then(response => {
        const pdfPath = Globals.BASE_URL + '/storage/' + response.data.path

        attendance.image_recipt = pdfPath;

        Swal.close();

        Swal.fire({
          icon: 'success',
          title: 'Recibo gerado com sucesso!',
          showConfirmButton: true,
          confirmButtonText: 'Abrir em uma nova guia',
          allowOutsideClick: false,
        })
          .then((result) => {
            if (result.isConfirmed) {
              window.open(pdfPath, '_blank');
            }
          });
      })
      .catch(error => {
        console.error(error)
      })
  };

  // Limpar validação quando dados mudarem
  useEffect(() => {
    if (validationError) {
      setValidationError('');
    }
  }, [selectedMedications, paymentMethods, attachedFiles]);

  const medicationTotal = calculateTotal();

  return (
    <Modal show={show} onHide={handleCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {
            currentStep === 1 ?
              'Registrar pagamento' :
              'Selecionar endereço de entrega'
          }
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-4">
          <Col className="text-center">
            <div className="d-flex align-items-center justify-content-center mb-2">
              <FontAwesomeIcon icon={faUser} className="me-2 primary-color" />
              <span>Paciente: <span>{attendance.name}</span></span>
            </div>

            <div className="d-flex align-items-center justify-content-center mb-2">
              <FontAwesomeIcon icon={faUserMd} className="me-2 primary-color" />
              <span>Médico: <span>{attendance.professional_name}</span></span>
            </div>
          </Col>
        </Row>

        {
          currentStep === 1 && (
            <>
              <div className="mb-4">
                <div className="d-flex align-items-center mb-3">
                  <FontAwesomeIcon icon={faPills} className="me-2 primary-color" />
                  <h5 className="mb-0">Medicamentos</h5>
                </div>

                {
                  loading ? (
                    <Loading />
                  ) : (
                    <>
                      {paidmedicines.length > 0 && (
                        <div className="mb-4">
                          <div className='d-flex justify-content-between align-items-center'>
                            <h6 className="my-3 text-success">Medicamentos pagos</h6>
                            <div style={{ width: '48%' }} className="d-flex gap-2">
                              <Button
                                variant="outline-success"
                                onClick={() => handleGenerateReceipt()}
                                title="Gerar recibo"
                                className="flex-fill"
                              >
                                <FontAwesomeIcon icon={faReceipt} /> Gerar Recibo
                              </Button>
                              <Button
                                variant="outline-danger"
                                onClick={() => handleCancelPayment()}
                                title="Cancelar pagamento"
                                className="flex-fill"
                              >
                                <FontAwesomeIcon icon={faCancel} /> Cancelar pagamento
                              </Button>
                            </div>
                          </div>

                          {/* Cabeçalho da tabela */}
                          <div className="d-flex justify-content-between align-items-center p-2 bg-light border rounded-top">
                            <div className="col-4 fw-bold">Nome</div>
                            <div className="col-2 fw-bold text-center">Quantidade</div>
                            <div className="col-2 fw-bold text-center">Preço Unitário</div>
                            <div className="col-2 fw-bold text-center">Total</div>
                          </div>

                          <ListGroup style={{ maxHeight: '300px', overflowY: 'auto' }} className="border-top-0">
                            {medicines
                              .filter(medicine => medicine.pharmacy_drug_flag_paid)
                              .map(medicine => (
                                <ListGroup.Item key={medicine.id} className="d-flex justify-content-between align-items-center">
                                  <div className="col-4">
                                    {medicine.pharmacy_drug_name}
                                  </div>
                                  <div className="col-2 text-center">
                                    <span className='fw-bold'>
                                      {medicine.pharmacy_drug_quantity}
                                    </span>
                                  </div>
                                  <div className="col-2 text-center">
                                    <span className="fw-bold text-success">
                                      {formatMoney(medicine.pharmacy_drug_price)}
                                    </span>
                                  </div>
                                  <div className="col-2 text-end">
                                    <span className="fw-bold text-success">
                                      {formatMoney(medicine.pharmacy_drug_quantity * medicine.pharmacy_drug_price)}
                                    </span>
                                  </div>
                                </ListGroup.Item>
                              ))
                            }
                          </ListGroup>

                          <div className="p-2 bg-light border border-top-0 rounded-bottom text-end">
                            <strong>
                              Total pago: {formatMoney(paidmedicines.reduce((total, med) => total + (Number(med.pharmacy_drug_price) || 0), 0))}
                            </strong>
                          </div>
                        </div>
                      )}

                      {notPaidMedicines.length > 0 && (
                        <div className="mb-4">
                          <div className="mb-3">
                            <h6 className="mb-3 text-secondary">Medicamentos Não Pagos</h6>

                            <Form.Check
                              type='checkbox'
                              id="select-all"
                              className='ms-3'
                              checked={selectedMedications.length === notPaidMedicines.length}
                              onChange={handleSelectAll}
                              label={selectedMedications.length === notPaidMedicines.length ? 'Desmarcar todos' : 'Selecionar todos'}
                            />

                          </div>

                          {/* Cabeçalho da tabela */}
                          <div className="d-flex justify-content-between align-items-center p-2 bg-light border rounded-top">
                            <div className="col-4 fw-bold">Nome</div>
                            <div className="col-2 fw-bold text-center">Quantidade</div>
                            <div className="col-2 fw-bold text-center">Preço Unitário</div>
                            <div className="col-2 fw-bold text-center">Total</div>
                          </div>

                          <ListGroup style={{ maxHeight: '300px', overflowY: 'auto' }} className="border-top-0">
                            {notPaidMedicines.map(medicine => (
                              <ListGroup.Item key={medicine.id} className="d-flex justify-content-between align-items-center">
                                <div className="col-4">
                                  <Form.Check
                                    type="checkbox"
                                    id={`med-${medicine.id}`}
                                    checked={selectedMedications.some(m => m.id === medicine.id)}
                                    onChange={() => handleMedicationChange(medicine)}
                                    label={medicine.pharmacy_drug_name}
                                  />
                                </div>

                                <div className="col-2 text-center">
                                  <span className='fw-bold'>
                                    {medicine.pharmacy_drug_quantity}
                                  </span>
                                </div>

                                <div className="col-2 text-end">
                                  <span className="fw-bold text-success">
                                    {formatMoney(medicine.pharmacy_drug_price)}
                                  </span>
                                </div>

                                <div className="col-2 text-center">
                                  <span className="fw-bold text-success">
                                    {formatMoney(medicine.pharmacy_drug_quantity * medicine.pharmacy_drug_price)}
                                  </span>
                                </div>
                              </ListGroup.Item>
                            ))
                            }
                          </ListGroup>
                        </div>
                      )}
                    </>
                  )
                }

                {
                  selectedMedications.length > 0 && (
                    <div className="mt-3 p-3 bg-light rounded text-end fs-5">
                      <strong>Valor total: {formatMoney(medicationTotal)}</strong>
                    </div>
                  )
                }
              </div>

              <div className="row mb-4 align-items-center">
                {/* <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="mb-0">
                    <img src={delivery} width={40} className="me-3" />
                    Taxa
                  </p>
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowMapModal(true)}
                    disabled={!selectedAddress}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                    Abrir Mapa
                  </Button>
                </div> */}

                <Col md={4} className='mt-2'>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <img src={delivery} width={40} className="me-3" alt="Icone de entrega"/>
                      Taxa
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="R$ 0,00"
                      value={deliveryFee}
                      onChange={(e) => setDeliveryFee(formatCurrency(e.target.value))}
                      disabled={notPaidMedicines.length === 0}
                    />
                  </Form.Group>
                </Col>

                {
                  selectedAddress && (
                    <Col md={4} className='mt-3'>
                    <Button
                        variant="outline-primary"
                        onClick={() => setShowMapModal(true)}
                      >
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                        Ver endereço
                      </Button>
                    </Col>
                  )
                }
              </div>

              <div className="mb-4">
                <h5 className="mb-3">Formas de Pagamento</h5>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <img src={money} width={40} alt="" />
                        Dinheiro
                      </Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="R$ 0,00"
                        value={paymentMethods.cash}
                        onChange={(e) => handlePaymentChange('cash', e.target.value)}
                        disabled={notPaidMedicines.length === 0}
                      />

                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <img src={card} width={40} alt="" />
                        Cartão
                      </Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="R$ 0,00"
                        value={paymentMethods.card}
                        onChange={(e) => handlePaymentChange('card', e.target.value)}
                        disabled={notPaidMedicines.length === 0}
                      />

                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <img src={pix} width={40} alt="" />
                        PIX
                      </Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="R$ 0,00"
                        value={paymentMethods.pix}
                        onChange={(e) => handlePaymentChange('pix', e.target.value)}
                        disabled={notPaidMedicines.length === 0}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {
                  medicationTotal !== 0 && (
                    <div className="p-3 bg-light rounded fs-5 text-end">
                      {calculateRemaining() === 0 ? (
                        <strong className="text-success">Pagamento completo!</strong>
                      ) : calculateRemaining() > 0 ? (
                        <strong className="text-warning">
                          Valor Faltante: <span>{formatMoney(calculateRemaining())}</span>
                        </strong>
                      ) : (
                        <strong className="text-danger">
                          Valor Excedente: <span>{formatMoney(Math.abs(calculateRemaining()))}</span>
                        </strong>
                      )}

                      {
                        deliveryFee && parseMaskedMoney(deliveryFee) > 0 && (
                          <div className="mt-2">
                            {/* <strong>
                              Taxa de entrega: {formatMoney(parseMaskedMoney(deliveryFee))}
                            </strong> */}

                            <hr />

                            <strong>
                              Total Geral: {formatMoney(medicationTotal + parseMaskedMoney(deliveryFee))}
                            </strong>
                          </div>
                        )
                      }
                    </div>
                  )
                }

              </div>

              <div className="mb-4">
                <h6 className="mb-3">Comprovantes <span className="text-danger">*</span></h6>
                <Form.Group>
                  <Form.Label htmlFor="file-upload" className="btn btn-outline-secondary">
                    <FontAwesomeIcon icon={faPaperclip} className="me-2" />
                    Anexar Comprovante(s)
                  </Form.Label>
                  <Form.Control
                    id="file-upload"
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={handleFileAttach}
                    style={{ display: 'none' }}
                    disabled={notPaidMedicines.length === 0}
                  />
                </Form.Group>

                {attachedFiles.length > 0 && (
                  <div className="mt-3">
                    {attachedFiles.map((fileItem, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center p-3 border rounded mb-2">
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="small fw-bold col-md-3">{fileItem.file.name}</span>

                            <div>
                              <small className="text-muted d-block mb-1">Método de pagamento:</small>
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant={fileItem.paymentMethod ? "outline-primary" : "outline-secondary"}
                                  size="sm"
                                  className="w-100"
                                >
                                  {getPaymentMethodLabel(fileItem.paymentMethod)}
                                  {/* <FontAwesomeIcon icon={faChevronDown} className="ms-2" /> */}
                                </Dropdown.Toggle>

                                <Dropdown.Menu className="w-100">
                                  {getPaymentMethodOptions().map((option) => (
                                    <Dropdown.Item
                                      key={option.value}
                                      onClick={() => handleFilePaymentMethodChange(index, option.value)}
                                      className="d-flex align-items-center"
                                    >
                                      <img src={option.icon} width={20} className="me-2" alt="" />
                                      {option.label}
                                    </Dropdown.Item>
                                  ))}
                                  {getPaymentMethodOptions().length === 0 && (
                                    <Dropdown.Item disabled>
                                      Nenhum método de pagamento disponível
                                    </Dropdown.Item>
                                  )}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>

                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeAttachment(index)}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </Button>
                          </div>

                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {validationError && (
                <Alert variant="danger" className="mb-3">
                  {validationError}
                </Alert>
              )}
            </>
          )
        }

        {currentStep === 2 && (
          <div className="mb-4">
            <h5 className="mb-3">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 primary-color " />
              Selecione o endereço de entrega
            </h5>

            {
              addresses.length > 0 ? (
                <Row>
                  {addresses.map(address => (
                    <Col md={12} key={address.id} className="mb-3">
                      <div
                        className={`card h-100 ${selectedAddress === address.id ? 'border-primary' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleAddressSelection(address.id)}
                      >
                        <div className="card-body">
                          <div className="d-flex align-items-start">
                            <Form.Check
                              type="radio"
                              name="address"
                              checked={selectedAddress === address.id}
                              onChange={() => handleAddressSelection(address.id)}
                              className="me-3 mt-1"
                            />
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="card-title mb-1">
                                  {address.street}, {address.number}
                                  {address.default && (
                                    <span className="badge bg-primary ms-2">Padrão</span>
                                  )}
                                </h6>
                              </div>

                              <div className="text-muted small">
                                {address.complement && (
                                  <div><strong>Complemento:</strong> {address.complement}</div>
                                )}
                                <div><strong>Bairro:</strong> {address.district}</div>
                                <div><strong>CEP:</strong> {formatCEP(address.zipcode)}</div>
                                <div><strong>Cidade:</strong> {address.city}-{address.state}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Row className='text-center gap-3'>
                  <h4 className='mt-3 text-danger'>Nenhum endereço encontrado para este paciente.</h4>
                  <h5>Cadastre um novo clicando no botão abaixo.</h5>
                </Row>
              )
            }

            <div className="mt-3">
              <Button variant="outline-primary" onClick={handleNewAddress}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Novo endereço
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {currentStep === 1 ? (
          <>
            <Button variant="secondary" onClick={handleCancel}>
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleNextStep}
              disabled={selectedMedications.length === 0}
            >
              Próximo
              <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={handlePreviousStep}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Voltar
            </Button>
            {/* <Button variant="secondary" onClick={handleCancel}>
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              Cancelar
            </Button> */}
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={addresses.length === 0 || !selectedAddress}
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              Salvar Pagamento
            </Button>
          </>
        )}
      </Modal.Footer>

      <Modal show={showAddressModal} onHide={handleCancelAddressForm}>
        <Modal.Header closeButton>
          <Modal.Title>Novo Endereço</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Endereço <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Digite o endereço"
                    value={addressForm.street}
                    onChange={(e) => handleAddressFormChange('street', e.target.value)}
                    isInvalid={!!addressFormErrors.street}
                  />
                  <Form.Control.Feedback type="invalid">
                    {addressFormErrors.street}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Número <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Número"
                    value={addressForm.number}
                    onChange={(e) => handleAddressFormChange('number', e.target.value)}
                    isInvalid={!!addressFormErrors.number}
                  />
                  <Form.Control.Feedback type="invalid">
                    {addressFormErrors.number}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Bairro <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Digite o bairro"
                    value={addressForm.district}
                    onChange={(e) => handleAddressFormChange('district', e.target.value)}
                    isInvalid={!!addressFormErrors.district}
                  />
                  <Form.Control.Feedback type="invalid">
                    {addressFormErrors.district}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>CEP <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="00000-000"
                    value={formatCEPInput(addressForm.zipcode)}
                    onChange={(e) => handleAddressFormChange('zipcode', e.target.value)}
                    maxLength={9}
                    isInvalid={!!addressFormErrors.zipcode}
                  />
                  <Form.Control.Feedback type="invalid">
                    {addressFormErrors.zipcode}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Cidade <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Digite a cidade"
                    value={addressForm.city}
                    onChange={(e) => handleAddressFormChange('city', e.target.value)}
                    isInvalid={!!addressFormErrors.city}
                  />
                  <Form.Control.Feedback type="invalid">
                    {addressFormErrors.city}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Estado <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="UF"
                    value={addressForm.state}
                    onChange={(e) => handleAddressFormChange('state', e.target.value.toUpperCase())}
                    maxLength={2}
                    isInvalid={!!addressFormErrors.state}
                  />
                  <Form.Control.Feedback type="invalid">
                    {addressFormErrors.state}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Complemento</Form.Label>
              <Form.Control
                type="text"
                placeholder="Apartamento, casa, etc. (opcional)"
                value={addressForm.complement}
                onChange={(e) => handleAddressFormChange('complement', e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="default-address"
                label="Marcar como endereço padrão"
                checked={addressForm.default}
                onChange={(e) => handleAddressFormChange('default', e.target.checked)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelAddressForm}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAddress}
            disabled={savingAddress}
          >
            {savingAddress ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Salvando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Salvar Endereço
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
            Localização da Entrega
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAddress && (() => {
            const address = addresses.find(addr => addr.id === selectedAddress);
            const fullAddress = `${address.street}, ${address.number}, ${address.district}, ${address.city}-${address.state}, ${address.zipcode}`;
            const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(address.zipcode)}&output=embed`;

            return (
              <>
                <div className="mb-3">
                  <strong>Endereço:</strong> {fullAddress}
                </div>
                <iframe
                  title="Mapa"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  src={mapUrl}
                />
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMapModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};